import { useCallback, useMemo, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import {
  runSendManyWhatsAppMessagesOrchestrator,
  runSendWhatsAppMessageOrchestrator,
} from "../helpers/sendWhatsAppMessageOrchestrator";
import type {
  SendManyWhatsAppMessageInput,
  SendMessageResult,
  SendWhatsAppMessageInput,
} from "../types";
import { useWhatsAppEndpoint } from "./useWhatsAppEndpoint";

type SendParams = Omit<SendWhatsAppMessageInput, "wabaId" | "phoneNumberId"> & {
  /**
   * Permite override explícito em casos especiais.
   * Quando omitido, o hook resolve via `useWhatsAppEndpoint`.
   */
  wabaId?: string | null;
  /**
   * Identificador preferencial para envio (Meta phone-number-id).
   * Quando omitido, o hook resolve via `useWhatsAppEndpoint`.
   */
  phoneNumberId?: string | null;
};

type SendManyParams = Omit<
  SendManyWhatsAppMessageInput,
  "wabaId" | "phoneNumberId"
> & {
  wabaId?: string | null;
  phoneNumberId?: string | null;
};

interface UseSendWhatsAppMessageParams {
  organizationId?: string;
  /**
   * Callback executado após envio bem-sucedido (ex.: atualizar estatísticas de franquia).
   */
  onMessageSent?: () => void;
}

interface UseSendWhatsAppMessageResult {
  send: (params: SendParams) => Promise<SendMessageResult>;
  /**
   * Envio para vários destinatários: 1 POST unitário (1 dest.) ou 1 POST em lote (2+ dest.).
   */
  sendMany: (params: SendManyParams) => Promise<SendMessageResult>;
  isSending: boolean;
  isSendingMany: boolean;
  hasError: boolean;
  errorMessage: string | null;
  lastResult: SendMessageResult | null;
  /**
   * Exposto para UI que queira mostrar status do carregamento do endpoint.
   */
  isEndpointLoading: boolean;
  wabaIdRank1: string | null;
  phoneNumberIdRank1: string | null;
}

/**
 * useSendWhatsAppMessage
 *
 * Hook de domínio para orquestrar validações + resolução de endpoint + envio.
 * Pode ser usado:
 * - sem UI (automações): chama `send(...)` ou `sendMany(...)` (lote = 1 HTTP quando 2+ dest.);
 * - com UI (Dialog): consome flags de loading/erro e desabilita ações.
 */
export function useSendWhatsAppMessage(
  params: UseSendWhatsAppMessageParams = {},
): UseSendWhatsAppMessageResult {
  const { wabaIdRank1, phoneNumberIdRank1, isLoading: isEndpointLoading } =
    useWhatsAppEndpoint({
    organizationId: params.organizationId,
  });

  const [lastResult, setLastResult] = useState<SendMessageResult | null>(null);
  const [inlineErrorMessage, setInlineErrorMessage] = useState<string | null>(
    null,
  );

  const endpointContext = useMemo(
    () => ({ wabaIdRank1, phoneNumberIdRank1 }),
    [phoneNumberIdRank1, wabaIdRank1],
  );

  const { run, loading: isSending, error } = useAsyncTask<
    [SendParams],
    SendMessageResult
  >({
    task: useCallback(
      async (sendParams) => {
        setInlineErrorMessage(null);
        const result = await runSendWhatsAppMessageOrchestrator(
          sendParams,
          endpointContext,
        );
        setLastResult(result);
        if (result.status === "error") {
          setInlineErrorMessage(result.errorMessage);
        } else if (params.onMessageSent) {
          // Aguarda 2 segundos para o backend processar e atualizar o contrato
          setTimeout(() => {
            params.onMessageSent?.();
          }, 2000);
        }
        return result;
      },
      [endpointContext, params],
    ),
  });

  const {
    run: runMany,
    loading: isSendingMany,
    error: sendManyError,
  } = useAsyncTask<[SendManyParams], SendMessageResult>({
    task: useCallback(
      async (manyParams) => {
        setInlineErrorMessage(null);
        const result = await runSendManyWhatsAppMessagesOrchestrator(
          manyParams,
          endpointContext,
        );
        setLastResult(result);
        if (result.status === "error") {
          setInlineErrorMessage(result.errorMessage);
        } else if (params.onMessageSent) {
          // Para envios em lote, dispara callback imediato e deixa o polling fazer o resto
          // O usuário pode clicar no botão de refresh se quiser forçar atualização
          setTimeout(() => {
            params.onMessageSent?.();
          }, 2000);
        }
        return result;
      },
      [endpointContext, params],
    ),
  });

  const errorMessage = useMemo(() => {
    if (inlineErrorMessage) return inlineErrorMessage;
    if (error || sendManyError) {
      return "Erro ao enviar mensagem. Tente novamente.";
    }
    return null;
  }, [error, inlineErrorMessage, sendManyError]);

  const send = useCallback(async (sendParams: SendParams) => run(sendParams), [
    run,
  ]);

  const sendMany = useCallback(
    async (manyParams: SendManyParams) => runMany(manyParams),
    [runMany],
  );

  return {
    send,
    sendMany,
    isSending,
    isSendingMany,
    hasError: Boolean(errorMessage),
    errorMessage,
    lastResult,
    isEndpointLoading,
    wabaIdRank1,
    phoneNumberIdRank1,
  };
}
